import React, { ReactElement, useState } from 'react';
import { Box } from '@mui/material';
import { OrganizationSummary, Event, LeaderRequest } from 'services/api';
import { FollowOrganizationSection } from './FollowOrganizationSection';
import { ModalTabs, ModalTab } from './ModalTabs';
import { MembersTab } from './MembersTab';
import { AboutTab } from './AboutTab';
import { OrganizationSelector, EventsSection } from './';

export interface OrganizationModalContentProps {
  availableOrganizations: OrganizationSummary[];
  selectedOrganizationId: string;
  displayName: string;
  displayLogoUrl: string;
  onOrganizationChange: (newOrganizationId: string) => void;
  showFollowButton: boolean;
  isLoading: boolean;
  events: Event[];
  isFollowed: boolean;
  onClose: () => void;
  orgLeaders: Array<{ id: string; firstName: string; lastName: string; orgRole: string }>;
  isLeadersExpanded: boolean;
  onToggleLeaders: () => void;
  isEligibleForLeaderRequest: boolean;
  hasPendingRequest: boolean;
  myLeaderRequest: LeaderRequest | null | undefined;
  organizationName: string;
  onOpenRoleModal: () => void;
  onEditRequest: () => void;
  onCancelRequest: () => Promise<void>;
  onAcceptInvite: (acceptedTerms: boolean) => Promise<void>;
  onDeclineInvite: () => Promise<void>;
  schoolTermsUrl?: string;
  schoolTermsDisplayName?: string;

  // CAM-669: membership + same-school gate + school name for About tab
  membershipStatus: 'none' | 'pending' | 'approved';
  isSameSchool: boolean;
  onRequestMembership: () => Promise<void>;
  onCancelMembershipRequest: () => Promise<void>;
  onLeaveOrganization: () => Promise<void>;

  // CAM-684
  isAcceptedLeader: boolean;
  onLeaveMyRole: () => Promise<void>;

  // About tab
  schoolName?: string;
  numEvents?: { pending: number; live: number; past: number };
}

export const OrganizationModalContent = ({
  availableOrganizations,
  selectedOrganizationId,
  displayName,
  displayLogoUrl,
  onOrganizationChange,
  showFollowButton,
  isLoading,
  events,
  isFollowed,
  onClose,
  orgLeaders,
  isLeadersExpanded,
  onToggleLeaders,
  isEligibleForLeaderRequest,
  hasPendingRequest,
  myLeaderRequest,
  organizationName,
  onOpenRoleModal,
  onEditRequest,
  onCancelRequest,
  onAcceptInvite,
  onDeclineInvite,
  schoolTermsUrl,
  schoolTermsDisplayName,
  membershipStatus,
  isSameSchool,
  onRequestMembership,
  onCancelMembershipRequest,
  onLeaveOrganization,
  isAcceptedLeader,
  onLeaveMyRole,
  schoolName,
  numEvents,
}: OrganizationModalContentProps): ReactElement => {
  const [activeTab, setActiveTab] = useState<ModalTab>('events');

  // Other-school orgs don't expose membership/leader UI, so the Members tab is
  // hidden; never let it remain the active tab in that case.
  const effectiveTab: ModalTab =
    activeTab === 'members' && !isSameSchool ? 'events' : activeTab;

  return (
    <Box sx={{ flex: 1, overflowY: 'auto' }}>
      {/* Pinned organization header above tabs (CAM-669) */}
      <OrganizationSelector
        availableOrganizations={availableOrganizations}
        selectedOrganizationId={selectedOrganizationId}
        displayName={displayName}
        displayLogoUrl={displayLogoUrl}
        onOrganizationChange={onOrganizationChange}
      />

      <ModalTabs
        active={effectiveTab}
        onChange={setActiveTab}
        showMembers={isSameSchool}
      />

      {effectiveTab === 'events' && (
        // CAM-670: Events tab — Follow banner + events list only.
        // Spacing tightened to 16px and Upcoming Events heading moved to
        // weight 600 inside EventsSection.
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {showFollowButton && (
            <FollowOrganizationSection organizationId={selectedOrganizationId} />
          )}
          <EventsSection
            isLoading={isLoading}
            events={events}
            displayName={displayName}
            isFollowed={isFollowed}
            organizationId={selectedOrganizationId}
            onClose={onClose}
          />
        </Box>
      )}

      {effectiveTab === 'members' && (
        <MembersTab
          membershipStatus={membershipStatus}
          organizationName={organizationName}
          orgLeaders={orgLeaders}
          isLeadersExpanded={isLeadersExpanded}
          onToggleLeaders={onToggleLeaders}
          isEligibleForLeaderRequest={isEligibleForLeaderRequest}
          hasPendingLeaderRequest={hasPendingRequest}
          myLeaderRequest={myLeaderRequest}
          onOpenRoleModal={onOpenRoleModal}
          onEditLeaderRequest={onEditRequest}
          onCancelLeaderRequest={onCancelRequest}
          onAcceptLeaderInvite={onAcceptInvite}
          onDeclineLeaderInvite={onDeclineInvite}
          schoolTermsUrl={schoolTermsUrl}
          schoolTermsDisplayName={schoolTermsDisplayName}
          onRequestMembership={onRequestMembership}
          onCancelMembershipRequest={onCancelMembershipRequest}
          onLeaveOrganization={onLeaveOrganization}
          isSameSchool={isSameSchool}
          isAcceptedLeader={isAcceptedLeader}
          onLeaveMyRole={onLeaveMyRole}
        />
      )}

      {effectiveTab === 'about' && (
        <AboutTab
          organizationName={organizationName}
          schoolName={schoolName}
          numEvents={numEvents}
        />
      )}

    </Box>
  );
};
